import { Search, X, Clock, BellRing, CalendarDays, Loader2, Download } from "lucide-react";
import { downloadCsv } from "./exportCsv";

const plural = (n) => `enquir${n === 1 ? "y" : "ies"}`;

function ActionButton({ onClick, busy, icon: Icon, title, testId, children, className = "" }) {
  return (
    <button
      onClick={onClick}
      disabled={busy}
      className={`inline-flex items-center gap-1.5 text-sm font-semibold text-[#0A2540] bg-white border border-slate-200 hover:bg-slate-50 px-4 py-2 rounded-full transition-colors disabled:opacity-50 ${className}`}
      title={title}
      data-testid={testId}
    >
      {busy ? <Loader2 size={15} className="animate-spin" /> : <Icon size={15} />} {children}
    </button>
  );
}

export function StatsBar({ total, awaitingCount, overdueCount, sending, onReminder, onDigest }) {
  return (
    <div className="mb-6 flex flex-wrap items-center gap-3">
      <p className="text-[#64748B]" data-testid="admin-count">{total} {plural(total)}</p>
      {awaitingCount > 0 && (
        <span className="inline-flex items-center gap-1 text-xs font-bold text-[#B45309] bg-[#F59E0B]/15 rounded-full px-3 py-1" data-testid="awaiting-count">
          <Clock size={11} /> {awaitingCount} awaiting reply
        </span>
      )}
      {overdueCount > 0 && (
        <span className="inline-flex items-center gap-1 text-xs font-bold text-white bg-[#DC2626] rounded-full px-3 py-1" data-testid="overdue-count">
          <BellRing size={11} /> {overdueCount} overdue (2+ days)
        </span>
      )}
      <ActionButton onClick={onReminder} busy={sending.reminder} icon={BellRing} testId="send-reminder-now" className="ml-auto"
        title="A reminder is emailed automatically every morning at 8am; use this to send one now.">
        Email me the overdue list
      </ActionButton>
      <ActionButton onClick={onDigest} busy={sending.digest} icon={CalendarDays} testId="send-digest-now"
        title="A weekly summary is emailed automatically every Monday at 8am; use this to send last week's now.">
        Send weekly summary
      </ActionButton>
    </div>
  );
}

function FilterPill({ children, onClear, label, className = "", testId }) {
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1 ${className}`} data-testid={testId}>
      {children}
      <button onClick={onClear} className="hover:text-[#DC2626]" aria-label={label}><X size={12} /></button>
    </span>
  );
}

export function FilterBar({ f }) {
  return (
    <>
      <div className="mb-4 relative" data-testid="enquiry-search-wrap">
        <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
        <input
          type="search"
          value={f.query}
          onChange={(ev) => f.setQuery(ev.target.value)}
          placeholder="Search by parent name, email, phone or message…"
          className="w-full rounded-full border border-slate-200 bg-white pl-11 pr-4 py-3 text-sm text-[#0F172A] placeholder:text-[#94A3B8] focus:outline-none focus:border-[#FF7A1A] focus:ring-2 focus:ring-[#FF7A1A]/20"
          data-testid="enquiry-search"
        />
      </div>

      {f.hasFilters && (
        <div className="mb-4 flex flex-wrap items-center gap-2 text-sm" data-testid="active-filters">
          <span className="text-[#64748B]">Showing <strong className="text-[#0F172A]">{f.activeList.length}</strong> of {f.tabList.length}</span>
          {f.q && <FilterPill onClear={() => f.setQuery("")} label="Clear search" className="bg-slate-100 text-[#334155]">“{f.query.trim()}”</FilterPill>}
          {f.classFilter && <FilterPill onClear={() => f.setClassFilter(null)} label="Clear class filter" className="bg-[#0EA5E9]/10 text-[#0EA5E9] font-semibold" testId="class-filter-pill">{f.classFilter}</FilterPill>}
          {f.sourceFilter && <FilterPill onClear={() => f.setSourceFilter(null)} label="Clear source filter" className="bg-[#8B5CF6]/10 text-[#6D28D9] font-semibold">via {f.sourceFilter}</FilterPill>}
          {f.hideReplied && <FilterPill onClear={() => f.setHideReplied(false)} label="Show replied" className="bg-[#F59E0B]/15 text-[#B45309] font-semibold">Awaiting reply only</FilterPill>}
          <button onClick={f.clearFilters} className="font-semibold text-[#FF7A1A] hover:underline" data-testid="clear-all-filters">Clear all</button>
        </div>
      )}

      <div className="flex items-center gap-2 mb-6 border-b border-slate-200" data-testid="admin-tabs">
        {[["enquiries", `Enquiries (${f.realEnquiries.length})`], ["tests", `Tests (${f.testEnquiries.length})`]].map(([id, label]) => (
          <button
            key={id}
            onClick={() => f.setTab(id)}
            className={`px-4 py-2.5 text-sm font-bold -mb-px border-b-2 transition-colors ${f.tab === id ? "border-[#FF7A1A] text-[#0F172A]" : "border-transparent text-[#94A3B8] hover:text-[#334155]"}`}
            data-testid={`tab-${id}`}
          >
            {label}
          </button>
        ))}
        <label className="ml-auto inline-flex items-center gap-2 text-sm font-semibold text-[#64748B] cursor-pointer select-none pb-1">
          <input
            type="checkbox"
            checked={f.hideReplied}
            onChange={(e) => f.setHideReplied(e.target.checked)}
            className="w-4 h-4 accent-[#FF7A1A]"
            data-testid="hide-replied-toggle"
          />
          Hide replied
        </label>
        <button
          onClick={() => downloadCsv(f.activeList, f.hasFilters ? "enquiries-filtered" : "enquiries")}
          disabled={f.activeList.length === 0}
          className="ml-3 mb-1 inline-flex items-center gap-1.5 text-sm font-semibold text-[#0A2540] bg-white border border-slate-200 hover:bg-slate-50 px-3.5 py-1.5 rounded-full transition-colors disabled:opacity-40"
          title="Download the enquiries currently shown as a spreadsheet (CSV)"
          data-testid="export-csv"
        >
          <Download size={14} /> Export {f.activeList.length} to spreadsheet
        </button>
      </div>
    </>
  );
}
